import GeoJSON from 'ol/format/GeoJSON'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { Fill, Stroke, Style } from 'ol/style'

import { attentionColor } from '../lib/attention'
import { hexA, issueCountColor, lensColor } from '../lib/lens'

import type { MandalAggregate } from '../api/types'
import type { StyleFunction } from 'ol/style/Style'

export const MANDAL_LAYER_ID = 'gis-mandals'

/**
 * What the choropleth shades by, per active tab: the unified **Attention Score** on Overview (the
 * ranking spine), the FILTER-REACTIVE issue count on Issues, or the Coverage / Officers percentage.
 * All FIXED bands (D1 + D4).
 */
export type MapMetric = 'attention' | 'issues' | 'coverage' | 'officers'

export interface MandalStyleState {
  metric: MapMetric
  mandals: MandalAggregate[]
  /** Pre-computed attention score per mandal (used when `metric === 'attention'`). */
  attentionByName: Map<string, number>
  /** Filter-reactive issue count per mandal (used when `metric === 'issues'`). 0/absent → grey. */
  issueCountByName: Map<string, number>
  /** The multi-select set of selected mandal keys — every member gets the highlight stroke. */
  selectedNames: ReadonlySet<string>
  /** "Coverage shade" toggle — false renders near-transparent fills. */
  shade: boolean
}

/**
 * Style factory: rebuild + layer.setStyle() whenever metric/data/selection/shade change. The fill is
 * the active metric in FIXED, decision-meaningful bands — stable across periods, so "dark = act now"
 * means the same thing every time. Overview/Issues shade by attention; Coverage/Officers by %.
 */
export function mandalStyleFn({ metric, mandals, attentionByName, issueCountByName, selectedNames, shade }: MandalStyleState): StyleFunction {
  const byName = new Map(mandals.map((m) => [m.name, m] as const))
  const resolveFill = (name: string | undefined, m: MandalAggregate | undefined): string => {
    if (!shade) {
      return 'rgba(255,255,255,0.04)'
    }
    if (metric === 'attention') {
      return hexA(attentionColor(name !== undefined ? (attentionByName.get(name) ?? 0) : 0), 0.62)
    }
    if (metric === 'issues') {
      return hexA(issueCountColor(name !== undefined ? (issueCountByName.get(name) ?? 0) : 0), 0.62)
    }
    return m ? hexA(lensColor(metric, m), 0.62) : 'rgba(255,255,255,0.04)'
  }

  return (feature) => {
    const name = feature.get('Mandal') as string | undefined
    const m = name ? byName.get(name) : undefined
    const isSel = name !== undefined && selectedNames.has(name)
    return new Style({
      stroke: new Stroke({ color: isSel ? '#fde047' : 'rgba(30,58,138,0.55)', width: isSel ? 3 : 1.2 }),
      fill: new Fill({ color: resolveFill(name, m) }),
    })
  }
}

export async function buildMandalLayer(geojsonUrl: string): Promise<VectorLayer<VectorSource>> {
  const res = await fetch(geojsonUrl)
  if (!res.ok) {
    throw new Error(`Failed to load ${geojsonUrl}: ${res.status}`)
  }
  const geojson: unknown = await res.json()
  const source = new VectorSource({
    features: new GeoJSON().readFeatures(geojson, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    }),
  })
  const layer = new VectorLayer({ source, zIndex: 5 })
  layer.set('id', MANDAL_LAYER_ID)
  return layer
}
